// Settings types

import { Pagination } from './api';
import { UserRole } from './auth';

// General settings

/**
 * Company size bands accepted by PUT /settings/general.
 * Mirrors the backend `COMPANY_SIZES` tuple (app/utils/organization.py) — keep
 * both lists in sync or the server rejects the value with a 400.
 */
export const COMPANY_SIZES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+',
] as const;

export type CompanySize = (typeof COMPANY_SIZES)[number];

export interface GeneralSettings {
  tenant_name: string;
  logo_url: string | null;
  timezone: string;
  language: string;
  /**
   * The contracting entity's legal name — what appears on invoices (T&C 4.2).
   * Null on workspaces created before signup collected it; billing then falls
   * back to `tenant_name`.
   */
  legal_name: string | null;
  /** The customer's own VAT number — decides EU reverse charge. */
  tax_number?: string | null;
  /** ISO 3166-1 alpha-2, uppercase. Drives VAT place of supply. */
  country: string | null;
  /** Optional BI band; one of COMPANY_SIZES. */
  company_size: CompanySize | null;
}

export interface UpdateGeneralSettingsData {
  tenant_name?: string;
  logo_url?: string;
  timezone?: string;
  language?: 'en' | 'es' | 'fr' | 'de';
  /** Send null to clear it and fall back to the workspace name on invoices. */
  legal_name?: string | null;
  tax_number?: string | null;
  /** ISO 3166-1 alpha-2; the server stores it uppercase. */
  country?: string | null;
  company_size?: CompanySize | null;
}

// Team
export interface TeamMember {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
  joined_at: string;
}

export interface TeamListResponse {
  status: 'success';
  // The list is nested under data.members (reading data as the array crashed
  // the Team tab with a black screen, 2026-07-14).
  data: {
    members: TeamMember[];
    pagination: Pagination;
  };
  timestamp: string;
}

export interface InviteTeamMemberData {
  email: string;
  role: UserRole;
}

export interface UpdateTeamMemberRoleData {
  role: Exclude<UserRole, 'Owner'>;
}

export interface TransferOwnershipData {
  new_owner_id: string;
}

export interface TeamQueryParams {
  page?: number;
  limit?: number;
}

// ─── Learner Attributes (Roles / Departments / Locations) ───────────────

export interface LearnerAttribute {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  /** How many members currently have this attribute assigned (list endpoint). */
  learner_count?: number;
  created_at: string;
  updated_at: string;
}

export interface LearnerAttributeCreate {
  name: string;
  description?: string | null;
}

export interface LearnerAttributeUpdate {
  name?: string;
  description?: string | null;
}

export interface PaginatedAttributes {
  data: LearnerAttribute[];
  meta: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export type LearnerAttributeKind = 'roles' | 'departments' | 'locations';

// Notification preferences
export interface NotificationPreferences {
  email_notifications: boolean;
  push_notifications: boolean;
  course_reminders: boolean;
  system_updates: boolean;
  updated_at: string;
}

export interface UpdateNotificationPreferencesData {
  email_notifications?: boolean;
  push_notifications?: boolean;
  course_reminders?: boolean;
  system_updates?: boolean;
}

// Security
export interface SecuritySettings {
  auth_provider: 'email' | 'google_sso';
  last_password_change: string | null;
  can_change_password: boolean;
}

export interface SSOConfig {
  sso_enabled: boolean;
  sso_provider: string | null;
  client_id: string | null;
  client_secret: string | null;
}

export interface UpdateSSOConfigData {
  sso_enabled: boolean;
  sso_provider?: 'google' | 'microsoft' | 'okta' | 'auth0';
  client_id?: string;
  client_secret?: string;
}

